/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import {
    object, string, shape, func, arrayOf, number,

} from 'prop-types';
import { Divider, Grid } from '@material-ui/core';
import { withRouter } from 'react-router';
import { compose } from 'recompose';
import { withStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { getEnterpriseId } from '../../../../../../state/ducks/Member/ducks/EnterpriseId/EnterpriseId-Selectors';
import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getIsBot, getUserSubmittedProductFilterZipcode } from '../../../../../../state/ducks/App/App-Selectors';
import { getIsAuthenticatedStatus, getIsPassportUserLoggedIn } from '../../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getPassportData, getPassportSubscriptionStatus } from '../../../../../../state/ducks/Common/Common-Selectors';
import validatePassport from '../../../../../helpers/validatePassport';
import DynamicPrice from './DynamicPrice';
import DynamicPriceSkeleton from '../../../GraphqlSkeletonComponents/DynamicPriceSkeleton';
import PriceBreakDownUi from '../../../GraphqlCategoryPage/Partials/GraphqlProductContainer/Partials/GraphqlSimpleProduct/DesktopSimpleProduct/PriceBreakDownUi';
import CaPriceVariant from '../../../GraphqlCategoryPage/Partials/GraphqlProductContainer/Partials/GraphqlSimpleProduct/DesktopSimpleProduct/CaPriceTestType';
import { getPriceArrays } from '../../../../../helpers/DynamicPricing/getPriceArrays';
import bannerPromoHelper from '../../../../../helpers/bannerPromoHelper/bannerPromoHelper';
import useBannerPromoCode from '../../../../../helpers/bannerPromoHelper/useBannerPromoCode';

const styles = (theme) => ({
    paperBody: {
        boxSizing: 'border-box',
        marginBottom: '20px',
        letterSpacing: '0.24px',
        padding: theme?.palette?.pdp?.skus?.padding?.mobile || '0px 10px',
    },
    skuLabelName: {
        color: theme.palette.common?.black,
        fontSize: theme?.typography?.pdpSkuLabel?.fontSize || 'inherit',
        textTransform: 'uppercase',
        fontWeight: '600',
        lineHeight: '28px',
        margin: '4px 0px 8px 0px',
        textAlign: 'center',
    },
    skuLabelTag: {
        color: theme.palette.common?.black,
        lineHeight: '24px',
        margin: 0,
        fontStyle: 'italic',
        textAlign: 'center',
        maxWidth: '80%',
    },
    skuWrapper: {
        gridTemplateColumns: 'repeat(3,minmax(0,1fr))',
        display: 'grid',
        gap: '1rem',
        [theme.breakpoints.down('sm')]: {
            gap: '10px',
        },
    },
    badgeWrapper: {
        color: '#fff',
        fontWeight: '700',
        fontSize: '13px',
        textTransform: 'uppercase',
        lineHeight: '16.16px',
        backgroundColor: '#22724D',
        borderRadius: '2.424px',
        padding: '2px 8px',
        position: 'absolute',
        top: '-11px',
        textAlign: 'center',
        letterSpacing: '2.34px',
        whiteSpace: 'nowrap',
        [theme.breakpoints.down('sm')]: {
            fontSize: '10px',
            letterSpacing: '1.1px',
            padding: '2px 5px',
        },
    },
    crossedOutRetailPrice: {
        textDecoration: 'line-through',
        fontWeight: '500',
        fontSize: '24px',
        lineHeight: '36px',
        color: '#555',
        margin: 0,
        fontFamily: theme.typography?.secondaryFontFamily,
    },

    salePrice: {
        color: theme.palette.pdp.salePrice || '#22724D',
        margin: 0,
        fontSize: '24px',
        fontWeight: '500',
        lineHeight: '36px',
        fontFamily: theme.typography?.secondaryFontFamily,
        [theme.breakpoints.down('sm')]: {
            position: 'unset',
            display: 'block',
        },
    },
    skuHeader: {
        color: theme.palette.black,
        textAlign: 'left',
        lineHeight: '24px',
        fontWeight: 600,
        textTransform: 'uppercase',
        marginBottom: '22px',
    },
    skuBoxContainer: {
        padding: '16px 5px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        border: `1px solid ${theme.palette.pdp?.skus?.skuBorderColor || '#000'}`,
        position: 'relative',
        cursor: 'pointer',
        '&>span': {
            fontSize: '24px',
            fontWeight: 500,
            lineHeight: '36px',
            margin: 0,
            fontFamily: theme.typography?.secondaryFontFamily,
        },
    },
    selecedSkuBox: {
        border: `2px solid ${theme.palette.pdp?.skus?.skuBorderColor || '#000'}`,
        backgroundColor: '#FEF9C7',
    },
    retailPrice: {
        fontSize: '24px',
        fontWeight: 500,
        lineHeight: '36px',
        margin: 0,
        fontFamily: theme.typography?.secondaryFontFamily,
    },
    selectedRetailPrice: {
        fontSize: '24px',
        fontWeight: 500,
        lineHeight: '36px',
        margin: 0,
        fontFamily: theme.typography?.secondaryFontFamily,
    },
    divider: {
        marginTop: '32px',
        [theme.breakpoints.down('sm')]: {
            marginTop: '24px',
        },
    },
    priceWrapper: {
        marginTop: '20px',
    },
});

const ProductSkusWithCallOut = ({
    classes,
    skuData,
    trackEvent,
    selectedSku,
    handleSkuClick,
}) => {
    const enterpriseId = useSelector(getEnterpriseId);
    const isDynamicPriceEnabled = useSelector(getFeatureFlag('is-dynamic-pricing-enabled-pwa'));
    const isBannerCodePromoPricingEnabled = useSelector(getFeatureFlag('is-banner-code-promo-pricing-enabled'));
    const isZipDynamicPricingEnabled = useSelector(getFeatureFlag('is-zip-dynamic-pricing-enabled'));
    const userSubmittedZip = useSelector(getUserSubmittedProductFilterZipcode);
    const isPassportUser = useSelector(getIsPassportUserLoggedIn);
    const isAuthenticated = useSelector(getIsAuthenticatedStatus);
    const passportData = useSelector(getPassportData);
    const isPassportMember = validatePassport('isActivePassportUser', passportData);
    const isCaTransparencyPricing = useSelector(getFeatureFlag(['is-ca-transparency-all-in-pricing-enabled']));

    const [dynamicPriceIsLoading, setDynamicPriceLoading] = useState(true);
    const [productSkuPrices, setProductSkuPrices] = useState([]);
    const [isCaDelivery, setIsCaDelivery] = useState(false);
    const isBot = useSelector(getIsBot);
    const isPassportAdded = useSelector(getPassportSubscriptionStatus);
    const bannerPromoCode = useBannerPromoCode();
    const isBannerPromoPricingEnabled = isBannerCodePromoPricingEnabled && !!bannerPromoCode;
    const shouldUsePriceEngine = isDynamicPriceEnabled || isBannerPromoPricingEnabled;
    let customerTypeValue = [];
    if (isPassportUser || (isAuthenticated && isPassportMember) || isPassportAdded) {
        customerTypeValue = [{
            name: 'CUSTOMER_TYPE',
            value: 'Passport',
        }];
    } else if (isAuthenticated) {
        customerTypeValue = [{
            name: 'CUSTOMER_TYPE',
            value: 'Registered',
        }];
    }
    const pricingVariants = bannerPromoHelper.addPromotionCodeVariant(
        customerTypeValue,
        isBannerPromoPricingEnabled ? bannerPromoCode : '',
    );
    const dynamicPriceInstance = new DynamicPrice(skuData, enterpriseId, false, null, false, pricingVariants);
    const {
        getPriceRules,
    } = dynamicPriceInstance;

    useEffect(() => {
        if (isBot || !shouldUsePriceEngine) {
            setDynamicPriceLoading(false);
            return;
        }
        if (enterpriseId) {
            const useZipcode = isZipDynamicPricingEnabled;

            setDynamicPriceLoading(true);
            dynamicPriceInstance.call(useZipcode, userSubmittedZip).then((productPrices) => {
                const priceRules = getPriceRules(selectedSku?.id);
                // eslint-disable-next-line no-param-reassign
                selectedSku.priceRules = priceRules;
                setProductSkuPrices(productPrices?.newPrices || []);
                setIsCaDelivery(CaPriceVariant(productPrices, isCaTransparencyPricing));
                setDynamicPriceLoading(false);
            }).catch(() => {
                setDynamicPriceLoading(false);
            });
        }
    }, [
        setDynamicPriceLoading,
        setProductSkuPrices,
        userSubmittedZip,
        enterpriseId,
        bannerPromoCode,
    ]);
    /**
     * Renders the appropriate price component based on the prices.
     *
     * @param {Object} sku - The SKU object containing variant information.
     * @returns {JSX.Element} - JSX representing the rendered price component.
     */
    const renderSkuPrice = (sku) => {
        if (sku?.prices?.length === 0) return null;  // while prices are still being fetched

        if (shouldUsePriceEngine && dynamicPriceIsLoading) {
            return <span><DynamicPriceSkeleton priceLineOnly /></span>;
        }

        const prices = productSkuPrices.find((item) => item.id === sku.id)?.prices || sku?.prices;
        const priceArrays = getPriceArrays(prices);
        const regularPrice = priceArrays.retail[0]?.total || priceArrays.retail[0]?.value;
        const discountedPrice = priceArrays.sale[0]?.total || priceArrays.sale[0]?.value;
        if (!regularPrice && !discountedPrice) return null;
        return (
            <>
                {regularPrice > discountedPrice ? (
                    <>
                        <p className={classes.crossedOutRetailPrice}>${regularPrice}</p>
                        <p className={classes.salePrice}>${discountedPrice}</p>
                    </>
                ) : (
                    <p className={classes.retailPrice}>${regularPrice}</p>
                )}
            </>
        );
    };

    const handleSelectProduct = (variant) => {
        if (selectedSku.id === variant.id) return;
        handleSkuClick(variant.id);
        trackEvent({
            eventCategory: 'Product Page',
            eventAction: 'Size Selection',
            eventLabel: variant.name,
            item_variant: variant?.id || '',
        });
    };
    return (
        <div className={`${classes.paperBody} ProductSkusWithCallOut`}>
            <div className={classes.skuHeader}>
                Choose bouquet size
            </div>
            <Grid className={classes.skuWrapper}>
                {skuData.map((item) => {
                    const variantCallOutMsg = item?.attributes?.find((attr) => attr.name === 'variant_callout_messaging');
                    const variantBadging = item?.attributes?.find((attr) => attr.name === 'variant_badging');
                    return (
                        <Grid key={item.id} className={`${classes.skuBoxContainer} ${selectedSku.id === item.id && classes.selecedSkuBox}`} onClick={() => handleSelectProduct(item)}>
                            <div>
                                {renderSkuPrice(item)}
                            </div>

                            <p className={classes.skuLabelName}>{item.name}</p>
                            {variantCallOutMsg && <p className={classes.skuLabelTag}>{variantCallOutMsg?.values[0]?.value}</p>}
                            {variantBadging && <div className={classes.badgeWrapper}>{variantBadging?.values[0]?.value}</div>}
                        </Grid>
                    );
                })}
            </Grid>
            <div className={classes.priceWrapper}>
                <PriceBreakDownUi dynamicPriceData={productSkuPrices.length > 0 ? productSkuPrices.find((item) => selectedSku.id === item.id) : null} isCaDelivery={isCaDelivery} />
            </div>
            <Divider className={classes.divider} />
        </div>
    );
};

ProductSkusWithCallOut.propTypes = {
    classes: object.isRequired,
    skuData: arrayOf(
        shape({
            id: string.isRequired,
            name: string.isRequired,
            prices: arrayOf(
                shape({
                    type: string.isRequired,
                    value: number.isRequired,
                    __typename: string.isRequired,
                }),
            ).isRequired,
            __typename: string.isRequired,
        }),
    ).isRequired,
    selectedSku: shape({
        id: string.isRequired,
        name: string.isRequired,
        prices: arrayOf(
            shape({
                type: string.isRequired,
                value: number.isRequired,
                __typename: string.isRequired,
            }),
        ).isRequired,
        __typename: string.isRequired,
    }).isRequired,
    trackEvent: func.isRequired,
    handleSkuClick: func.isRequired,
};
const enhance = compose(
    withStyles(styles),
    withRouter,
);

export default enhance(ProductSkusWithCallOut);
